/**
 * EmbedView - Affichage des documents avec embeds natifs des réseaux sociaux
 *
 * Détecte automatiquement la plateforme depuis l'URL ou le champ source
 * et affiche l'embed natif correspondant (Twitter/X, Instagram, TikTok, YouTube, Facebook, LinkedIn)
 */

import React, { Suspense } from "react";
import { Content } from "@/models/document";
import {
  XEmbed,
  InstagramEmbed,
  TikTokEmbed,
  YouTubeEmbed,
  FacebookEmbed,
  LinkedInEmbed,
  PinterestEmbed,
} from "react-social-media-embed";
import NormalizeEmbed from "@/features/embed/normalize-embed";
import { Loading } from "@/components/ui/loading";

// ============================================================================
// Types
// ============================================================================

interface EmbedViewProps {
  items: Content[];
  displayMode?: string;
}

type SocialPlatform =
  | "twitter"
  | "instagram"
  | "tiktok"
  | "youtube"
  | "facebook"
  | "linkedin"
  | "pinterest"
  | "unknown";

// ============================================================================
// Platform Detection Utils
// ============================================================================

/**
 * Détecte la plateforme depuis l'URL du document
 */
const detectPlatformFromUrl = (url: string | undefined): SocialPlatform => {
  if (!url) return "unknown";

  const urlLower = url.toLowerCase();

  // Twitter / X
  if (
    urlLower.includes("twitter.com") ||
    urlLower.includes("x.com") ||
    urlLower.includes("t.co")
  ) {
    return "twitter";
  }

  // Instagram
  if (urlLower.includes("instagram.com") || urlLower.includes("instagr.am")) {
    return "instagram";
  }

  // TikTok
  if (urlLower.includes("tiktok.com") || urlLower.includes("vm.tiktok.com")) {
    return "tiktok";
  }

  // YouTube
  if (
    urlLower.includes("youtube.com") ||
    urlLower.includes("youtu.be") ||
    urlLower.includes("youtube-nocookie.com")
  ) {
    return "youtube";
  }

  // Facebook
  if (
    urlLower.includes("facebook.com") ||
    urlLower.includes("fb.com") ||
    urlLower.includes("fb.watch")
  ) {
    return "facebook";
  }

  // LinkedIn
  if (urlLower.includes("linkedin.com")) {
    return "linkedin";
  }

  // Pinterest
  if (urlLower.includes("pinterest.com") || urlLower.includes("pin.it")) {
    return "pinterest";
  }

  return "unknown";
};

/**
 * Détecte la plateforme depuis le champ source ou type du document
 */
const detectPlatformFromSource = (
  source: string | undefined
): SocialPlatform => {
  if (!source) return "unknown";

  const sourceLower = source.toLowerCase();

  if (sourceLower.includes("twitter") || sourceLower.includes("x")) {
    return "twitter";
  }
  if (sourceLower.includes("instagram")) {
    return "instagram";
  }
  if (sourceLower.includes("tiktok")) {
    return "tiktok";
  }
  if (sourceLower.includes("youtube")) {
    return "youtube";
  }
  if (sourceLower.includes("facebook")) {
    return "facebook";
  }
  if (sourceLower.includes("linkedin")) {
    return "linkedin";
  }
  if (sourceLower.includes("pinterest")) {
    return "pinterest";
  }

  return "unknown";
};

/**
 * Détecte la plateforme d'un document (URL en priorité, puis source)
 */
const detectPlatform = (item: Content): SocialPlatform => {
  const rawData = item.normalized ? item.normalized : (item as any);
  const url = rawData.url;

  // Priorité 1: URL
  const platformFromUrl = detectPlatformFromUrl(url);
  if (platformFromUrl !== "unknown") {
    return platformFromUrl;
  }

  // Priorité 2: Champ source (peut être dans extra, metadata, ou directement sur l'item)
  const source =
    (item as any).source ||
    (item as any).type ||
    (item as any).extra?.source ||
    (item as any).metadata?.source;

  return detectPlatformFromSource(source);
};

import { safeString } from "@/utils/safe-value";

/**
 * Récupère l'URL du document
 */
const getDocumentUrl = (item: Content): string | undefined => {
  const rawData = item.normalized ? item.normalized : (item as any);
  const url = safeString(rawData.url);
  return url || undefined;
};

// ============================================================================
// Loading Component
// ============================================================================

const EmbedLoader: React.FC = () => (
  <div className="flex h-64 w-full items-center justify-center rounded-lg bg-surface-secondary">
    <Loading size={32} />
  </div>
);

// ============================================================================
// Single Embed Component
// ============================================================================

interface SingleEmbedProps {
  item: Content;
  platform: SocialPlatform;
  url: string;
}

const SingleEmbed: React.FC<SingleEmbedProps> = ({ item, platform, url }) => {
  const embedStyle = { width: "100%", maxWidth: 550 };

  switch (platform) {
    case "twitter":
      return (
        <Suspense fallback={<EmbedLoader />}>
          <XEmbed url={url} width="100%" />
        </Suspense>
      );

    case "instagram":
      return (
        <Suspense fallback={<EmbedLoader />}>
          <InstagramEmbed url={url} width="100%" captioned />
        </Suspense>
      );

    case "tiktok":
      return (
        <Suspense fallback={<EmbedLoader />}>
          <TikTokEmbed url={url} width="100%" />
        </Suspense>
      );

    case "youtube":
      return (
        <Suspense fallback={<EmbedLoader />}>
          <YouTubeEmbed url={url} width="100%" height={315} />
        </Suspense>
      );

    case "facebook":
      return (
        <Suspense fallback={<EmbedLoader />}>
          <FacebookEmbed url={url} width="100%" />
        </Suspense>
      );

    case "linkedin":
      return (
        <Suspense fallback={<EmbedLoader />}>
          <LinkedInEmbed url={url} width="100%" />
        </Suspense>
      );

    case "pinterest":
      return (
        <Suspense fallback={<EmbedLoader />}>
          <PinterestEmbed url={url} width="100%" />
        </Suspense>
      );

    default:
      // Fallback vers NormalizeEmbed pour les plateformes non supportées
      return (
        <NormalizeEmbed
          normalized={item.normalized || (item as any)}
          sentiment={item.extra?.sentiment}
        />
      );
  }
};

// ============================================================================
// Main Component
// ============================================================================

const EmbedView: React.FC<EmbedViewProps> = ({ items }) => {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {items.map((item, index) => {
        const platform = detectPlatform(item);
        const url = getDocumentUrl(item);

        return (
          <div
            key={item.id || index}
            className="flex items-start justify-center"
          >
            <div className="w-full max-w-[550px]">
              {url ? (
                <SingleEmbed item={item} platform={platform} url={url} />
              ) : (
                <NormalizeEmbed
                  normalized={item.normalized || (item as any)}
                  sentiment={item.extra?.sentiment}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EmbedView;
